import { useMutation, useQuery } from '@tanstack/react-query';
import {
  gitService,
  TERMINAL_JOB_STATUSES,
  type DevelopCommitsPage,
  type DevelopGitInfo,
  type PushJobStatus,
} from '../services/git.service';

/** The tenant's Pliable git repo + current develop head (for the Git settings tab). */
export function useDevelopInfo() {
  return useQuery<DevelopGitInfo>({
    queryKey: ['git-develop'],
    queryFn: () => gitService.getDevelop(),
    staleTime: 15_000,
  });
}

/** Kicks the background export→push job (Develop → git); returns the job id to poll. */
export function usePushDevelop() {
  return useMutation({
    mutationFn: () => gitService.pushDevelop(),
  });
}

/** Kicks the background import job (git → Develop); returns the job id to poll. */
export function usePullDevelop() {
  return useMutation({
    mutationFn: () => gitService.pullDevelop(),
  });
}

/** A page of develop-branch commits for the Git-tab history view. */
export function useDevelopCommits(page: number, perPage = 20) {
  return useQuery<DevelopCommitsPage>({
    queryKey: ['git-develop-commits', page, perPage],
    queryFn: () => gitService.getDevelopCommits(page, perPage),
    staleTime: 15_000,
  });
}

/** Polls a push/pull job until it reaches a terminal status. */
export function useGitJobStatus(jobId: string | null) {
  return useQuery<PushJobStatus>({
    queryKey: ['git-job', jobId],
    queryFn: () => gitService.getJobStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.data && TERMINAL_JOB_STATUSES.has(query.state.data.job_status) ? false : 1500,
  });
}

/** @deprecated use useGitJobStatus */
export const usePushJobStatus = useGitJobStatus;
